import { format, isToday, parseISO } from 'date-fns'

/**
 * Robustly parses a date string that may or may not carry timezone info.
 * - Handles space-separated datetime strings (e.g. "2024-01-01 12:00:00")
 * - Appends 'Z' when no timezone offset is present so the value is treated
 *   as UTC rather than local time.
 * - Returns null for missing/invalid input instead of an Invalid Date.
 */
export const parseDate = (value: string | Date | null | undefined): Date | null => {
  if (!value) return null

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }

  const normalised = value.includes('T') ? value : value.replace(' ', 'T')
  const iso = /Z|[+-]\d\d:?\d\d$/.test(normalised) ? normalised : `${normalised}Z`
  const parsed = parseISO(iso)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/**
 * Formats a date string for display in a chat bubble timestamp.
 * - Returns '—' when the input is missing or invalid.
 * - Shows only HH:mm for messages from today.
 * - Shows "MMM dd, HH:mm" for older messages.
 */
export const formatTime = (dateString: string | Date | null | undefined): string => {
  const date = parseDate(dateString as string)
  if (!date) return '—'
  if (isToday(date)) {
    return format(date, 'hh:mm a')
  }
  return format(date, 'MMM dd, hh:mm a')
}

/**
 * Formats a time string (e.g. "14:30", "14:30:00") or Date object into 12-hour AM/PM format (e.g. "02:30 PM").
 */
export const format12HourTime = (timeInput: string | Date | null | undefined): string => {
  if (!timeInput) return ''
  if (timeInput instanceof Date) {
    return Number.isNaN(timeInput.getTime()) ? '' : format(timeInput, 'hh:mm a')
  }
  const str = String(timeInput).trim()
  if (!str) return ''

  const timeMatch = str.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/)
  if (timeMatch) {
    const hours = parseInt(timeMatch[1], 10)
    const minutes = parseInt(timeMatch[2], 10)
    if (hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
      const d = new Date()
      d.setHours(hours, minutes, 0, 0)
      return format(d, 'hh:mm a')
    }
  }

  const d = parseDate(str)
  if (d) {
    return format(d, 'hh:mm a')
  }

  return str
}

/**
 * Formats a date string or Date object in dd-MMM-yyyy format (e.g., "07-Oct-2026").
 */
export const formatDateDDMMYYYY = (dateInput: string | Date | null | undefined): string => {
  if (!dateInput) return ''
  const d = parseDate(dateInput)
  if (!d) return ''
  return format(d, 'dd-MMM-yyyy')
}

/**
 * Formats custom datetime inputs (e.g. "MMM dd, yyyy, hh:mm a").
 * Accepts an optional fallback (default: '') when the value is missing or invalid.
 */
export const formatCustomDateTime = (
  value: string | Date | null | undefined,
  fallback = ''
): string => {
  if (!value) return fallback
  try {
    return format(value, 'MMM dd, yyyy, hh:mm a')
  } catch {
    return fallback || String(value)
  }
}

/**
 * Formats custom date inputs (e.g. "MMM dd, yyyy").
 */
export const formatCustomDate = (value: string | Date | null | undefined): string => {
  if (!value) return ''
  try {
    return format(value, 'MMM dd, yyyy')
  } catch {
    return String(value)
  }
}

/**
 * Formats a Date object into HTML5 datetime-local format (YYYY-MM-DDTHH:mm).
 */
export const formatDateTimeLocal = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

/**
 * Helper function to get default start date (Current time + 15 min rounded up to next 15-min mark)
 */
export const getDefaultStartDate = (baseDate: Date = new Date()): string => {
  const date = new Date(baseDate.getTime() + 15 * 60 * 1000)
  const minutes = date.getMinutes()
  const remainder = minutes % 15
  if (remainder !== 0) {
    date.setMinutes(minutes + (15 - remainder), 0, 0)
  } else {
    date.setSeconds(0, 0)
  }
  return formatDateTimeLocal(date)
}

/**
 * Helper function to get default end date (3 days after start date at 6:00 PM)
 */
export const getDefaultEndDate = (startIso?: string): string => {
  const startDate = startIso ? new Date(startIso) : new Date(getDefaultStartDate())
  const validStart = isNaN(startDate.getTime()) ? new Date() : startDate
  const endDate = new Date(validStart)
  endDate.setDate(endDate.getDate() + 3)
  endDate.setHours(18, 0, 0, 0)
  return formatDateTimeLocal(endDate)
}

/**
 * Returns a randomly selected time between 08:00 AM and 11:00 AM in 15-minute intervals.
 */
export const getRandomStartWindow = (): string => {
  const validSlots = [
    '08:00', '08:15', '08:30', '08:45',
    '09:00', '09:15', '09:30', '09:45',
    '10:00', '10:15', '10:30', '10:45',
    '11:00',
  ]
  const index = Math.floor(Math.random() * validSlots.length)
  return validSlots[index]
}

